import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import Swal from "sweetalert2";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";


const BoothModal = ({ booth, onClose, onReserve }) => {
    const message = "Hello, I would like to book a booth.";
    const [step, setStep] = useState(1);
    const theme = {
        overlay: "rgba(3, 6, 20, 0.78)",
        modalBg: "#111427",
        panelBg: "#181c33",
        panelSoft: "#1e2340",
        border: "rgba(233, 30, 140, 0.38)",
        borderStrong: "#E91E8C",
        text: "#F4F7FB",
        muted: "#A8B0C7",
        inputBg: "#0f1326",
        inputBorder: "rgba(233, 30, 140, 0.55)",
    };

    const fieldStyle = {
        width: "100%",
        background: theme.inputBg,
        border: `1px solid ${theme.inputBorder}`,
        borderRadius: "14px",
        color: theme.text,
        padding: "13px 16px",
        outline: "none",
        boxShadow: "none",
    };

    /* ================= STEP 1 ================= */
    const [form, setForm] = useState({
        name: "",
        email: "",
        company: "",
        phone: "",
    });

    /* ================= AMOUNT ================= */
    const boothPrices = {
        title: 30000,
        official: 25000,
        exclusive: 20000,
        regional: 15000,
        diamond: 12000,
        premium: 10000,
        gold: 6000,
        silver: 4000,
        podcast: 0,
    };

    const sponsorshipPrices = {
        cocktail: 5000,
        cafe: 5000,
        networklounge: 5000,
        speakerhall: 10000,
        entrancearch: 5000,
        registrationarea: 5000,
        ledwalladvertisement: 5000,
        rollupbanner: 250,
        flagpole: 500,
        attendeebag: 3000,
    };


    const isSponsorship = booth.boothType in sponsorshipPrices;

    const amount = isSponsorship
        ? sponsorshipPrices[booth.boothType]
        : boothPrices[booth.boothType];

    const total = amount ?? 0;


    const handlePhoneChange = (value) => {
        setForm({ ...form, phone: value });
    };




    /* ================= STEP 2 ================= */
    const [paymentMethod, setPaymentMethod] = useState(null);
    const [paymentImage, setPaymentImage] = useState(null);

    /* ================= EFFECTS ================= */
    useEffect(() => {
        const handleEsc = (e) => e.key === "Escape" && onClose();
        window.addEventListener("keydown", handleEsc);
        document.body.style.overflow = "hidden";

        return () => {
            window.removeEventListener("keydown", handleEsc);
            document.body.style.overflow = "";
        };
    }, [onClose]);

    /* ================= VALIDATION ================= */
    const validateStep1 = () => {
        if (!form.name || !form.email || !form.company || !form.phone) {
            Swal.fire({
                toast: true,
                icon: "error",
                title: "Please fill all required fields",
                position: "top-end",
                showConfirmButton: false,
                timer: 3000,
                customClass: { container: "swal2-toast-container-high-z" },
            });
            return false;
        }
        return true;
    };
           const handleSubmit = async () => {
    if (!paymentMethod || !paymentImage) {
        Swal.fire({
            toast: true,
            icon: "warning",
            title: "Payment method and proof are required",
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
        });
        return;
    }

    try {
        const formData = new FormData();

        // ===== BASIC DETAILS =====
        formData.append("name", form.name);
        formData.append("email", form.email);
        formData.append("phone", form.phone);
        formData.append("company", form.company || "");

        // ===== OPTIONAL =====
        formData.append("referal_code", form.referal || "");

        // ===== BOOTH DETAILS =====
        formData.append("boothno", booth.boothNo || "");
        formData.append("boothtitle", booth.title || "");
        formData.append("boothsize", booth.size || "");
        formData.append("boothammount", total);

        // ===== PAYMENT =====
        formData.append("paymenttype", paymentMethod);
        formData.append(
            "networktype",
            paymentMethod === "usdt" ? "TRC20/ERC20" : ""
        );
        formData.append("file", paymentImage);

        formData.append("api_key", process.env.REACT_APP_API_KEY);

        const response = await fetch(
            `${process.env.REACT_APP_API_URL}/floorplansubmit`,
            {
                method: "POST",
                headers: {
                    Accept: "application/json",
                },
                body: formData,
            }
        );

        const result = await response.json();

        if (result.code === 1) {
            Swal.fire({
                icon: "success",
                title: "Booth Reserved Successfully!",
                text: "Payment submitted. We will verify shortly.",
            }).then(() => {
                // Reload the page after user closes the alert
                window.location.reload();
            });
            onClose();
        } else {
            Swal.fire({
                icon: "error",
                title: "Error",
                text: result.msg || "Something went wrong",
            });
        }
    } catch (error) {
        console.error(error);
        Swal.fire({
            icon: "error",
            title: "Server Error",
            text: "Please try again later",
        });
    }
};



    return createPortal(
        <div
            className="ticket-custom-modal-overlay"
            onClick={onClose}
            style={{ background: theme.overlay, backdropFilter: "blur(8px)" }}
        >
            <div
                className="ticket-custom-modal"
                onClick={(e) => e.stopPropagation()}
                style={{
                    background: theme.modalBg,
                    color: theme.text,
                    border: `1px solid ${theme.border}`,
                    boxShadow: "0 28px 80px rgba(0,0,0,0.55)",
                    borderRadius: "24px",
                }}
            >
                <style>{`
                    .ticket-custom-modal .payment-card {
                        background: #fff !important;
                    }
                    .ticket-custom-modal .payment-card.active {
                        background: #fff !important;
                    }
                `}</style>
                {/* HEADER */}
                <div
                    className="modal-header d-flex justify-content-between align-items-center"
                    style={{
                        borderBottom: `1px solid ${theme.border}`,
                        paddingBottom: "18px",
                    }}
                >
                    <h4 className="m-0" style={{ color: theme.text }}>{booth.title} Reservation</h4>
                    <button
                        className="modal-close"
                        onClick={onClose}
                        style={{
                            width: "42px",
                            height: "42px",
                            borderRadius: "50%",
                            border: `1px solid ${theme.borderStrong}`,
                            background: "rgba(233, 30, 140, 0.12)",
                            color: theme.borderStrong,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* STEPS */}
                <div className="steps d-flex justify-content-center my-4">
                    {[1, 2].map((s) => (
                        <div
                            key={s}
                            className={`step ${step >= s ? "active" : ""}`}
                            style={{
                                minWidth: "110px",
                                textAlign: "center",
                                padding: "10px 18px",
                                margin: "0 8px",
                                borderRadius: "999px",
                                border: `1px solid ${step >= s ? theme.borderStrong : "rgba(255,255,255,0.12)"}`,
                                background: step >= s ? "rgba(233, 30, 140, 0.18)" : theme.panelBg,
                                color: step >= s ? "#FFD6EA" : theme.muted,
                                fontWeight: 700,
                            }}
                        >
                            Step {s}
                        </div>
                    ))}
                </div>

                {/* ================= STEP 1 ================= */}
                {step === 1 && (
                    <div className="step-content">
                        <div className="row g-4">
                            {/* LEFT – FORM */}
                            <div className="col-md-7">
                                <div className="row g-3">
                                    {["name", "email", "company", "phone", "referal"].map((field, i) => (
                                        <div className="col-md-12" key={i}>
                                            {field === "phone" ? (
                                                <PhoneInput
                                                    country={"ae"}
                                                    value={form.phone}
                                                    onChange={handlePhoneChange}
                                                    inputProps={{
                                                        name: "phone",
                                                        required: true,
                                                        placeholder: "Phone Number",
                                                    }}
                                                    containerStyle={{ width: "100%" }}
                                                    inputStyle={{
                                                        ...fieldStyle,
                                                        height: "50px",
                                                        paddingLeft: "52px",
                                                    }}
                                                    buttonStyle={{
                                                        border: `1px solid ${theme.inputBorder}`,
                                                        borderRight: "none",
                                                        background: theme.inputBg,
                                                        borderTopLeftRadius: "14px",
                                                        borderBottomLeftRadius: "14px",
                                                    }}
                                                    specialLabel=""
                                                />
                                            ) : (
                                                <input
                                                    type={field === "email" ? "email" : "text"}
                                                    placeholder={field.charAt(0).toUpperCase() + field.slice(1)}
                                                    value={form[field]}
                                                    onChange={(e) =>
                                                        setForm({ ...form, [field]: e.target.value })
                                                    }
                                                    required
                                                    style={fieldStyle}
                                                />
                                            )}
                                        </div>
                                    ))}

                                </div>
                            </div>

                            {/* RIGHT – SUMMARY */}
                            <div className="col-md-5">
                                <div
                                    className="summary-box p-4 rounded sticky-top"
                                    style={{
                                        background: theme.panelBg,
                                        border: `1px solid ${theme.border}`,
                                        color: theme.text,
                                    }}
                                >
                                    <h6 style={{ color: theme.text }}>
                                        {isSponsorship ? "Sponsorship Summary" : "Booth Summary"}
                                    </h6>

                                    {!isSponsorship && (
                                        <p>
                                            Booth No: <b>{booth.boothNo}</b>
                                        </p>
                                    )}

                                    <p>
                                        {isSponsorship ? "Sponsorship Title" : "Booth Title"}:{" "}
                                        <b>{booth.title}</b>
                                    </p>

                                    {/* Show size ONLY if exists */}
                                    {booth.size && (
                                        <p>
                                            {isSponsorship ? "Sponsorship Size" : "Booth Size"}:{" "}
                                            <b>{booth.size}</b>
                                        </p>
                                    )}

                                    <p>
                                        {isSponsorship ? "Sponsorship Amount" : "Booth Amount"}: ${amount}
                                    </p>

                                    <hr style={{ borderColor: "rgba(255,255,255,0.12)" }} />

                                    <h5 style={{ color: "#FFD6EA" }}>Total Payable: ${total}</h5>

                                </div>
                                <div
                                    className="summary-box p-4 rounded mt-5"
                                    style={{
                                        background: theme.panelSoft,
                                        border: `1px solid ${theme.border}`,
                                        color: theme.text,
                                    }}
                                >
                                    <h6 style={{ color: theme.text }}>Contact us for Booking support</h6>
                                    <p className="mt-3">
                                        Whatsapp: <b><a
                                            style={{ color: "#FFD6EA" }}
                                            href={`https://wa.me/971588845033?text=${encodeURIComponent(message)}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            +971 58 884 5033
                                        </a></b>
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="modal-footer d-flex justify-content-between mt-4">
                            <button className="btn btn-outline-secondary" onClick={onClose}>
                                Close
                            </button>
                            <button
                                className="btn bg-pink text-white"
                                onClick={() => validateStep1() && setStep(2)}
                            >
                                Continue
                            </button>
                        </div>
                    </div>
                )}

                {/* ================= STEP 2 ================= */}
                {step === 2 && (
                    <div className="step-content" style={{ color: theme.text }}>
                        <h5 className="mb-4" style={{ color: theme.text }}>Choose Payment Method</h5>

                        {/* PAYMENT METHODS */}
                        <div className="payment-grid" style={{ gap: "16px" }}>
                            <div
                                className={`payment-card ${paymentMethod === "now" ? "active" : ""}`}
                                onClick={() => setPaymentMethod("now")}
                                style={{
                                    background: "#ffffff",
                                    border: `1px solid ${paymentMethod === "now" ? theme.borderStrong : "rgba(17,20,39,0.08)"}`,
                                    borderRadius: "18px",
                                    padding: "18px",
                                    minHeight: "108px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    boxShadow: paymentMethod === "now"
                                        ? "0 0 0 2px rgba(233, 30, 140, 0.14), 0 12px 24px rgba(0,0,0,0.08)"
                                        : "0 10px 24px rgba(0,0,0,0.08)",
                                }}
                            >
                                <img src={process.env.PUBLIC_URL + "/assets/images/payments/nowpayments.png"} alt="NowPayments" />
                            </div>

                            <div
                                className={`payment-card ${paymentMethod === "stripe" ? "active" : ""}`}
                                onClick={() => setPaymentMethod("stripe")}
                                style={{
                                    background: "#ffffff",
                                    border: `1px solid ${paymentMethod === "stripe" ? theme.borderStrong : "rgba(17,20,39,0.08)"}`,
                                    borderRadius: "18px",
                                    padding: "18px",
                                    minHeight: "108px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    boxShadow: paymentMethod === "stripe"
                                        ? "0 0 0 2px rgba(233, 30, 140, 0.14), 0 12px 24px rgba(0,0,0,0.08)"
                                        : "0 10px 24px rgba(0,0,0,0.08)",
                                }}
                            >
                                <img src={process.env.PUBLIC_URL + "/assets/images/payments/stripe.png"} alt="Stripe" />
                            </div>

                            <div
                                className={`payment-card ${paymentMethod === "upi" ? "active" : ""}`}
                                onClick={() => setPaymentMethod("upi")}
                                style={{
                                    background: "#ffffff",
                                    border: `1px solid ${paymentMethod === "upi" ? theme.borderStrong : "rgba(17,20,39,0.08)"}`,
                                    borderRadius: "18px",
                                    padding: "18px",
                                    minHeight: "108px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    boxShadow: paymentMethod === "upi"
                                        ? "0 0 0 2px rgba(233, 30, 140, 0.14), 0 12px 24px rgba(0,0,0,0.08)"
                                        : "0 10px 24px rgba(0,0,0,0.08)",
                                }}
                            >
                                <img src={process.env.PUBLIC_URL + "/assets/images/payments/upi.png"} alt="UPI" />
                            </div>

                            <div
                                className={`payment-card ${paymentMethod === "usdt" ? "active" : ""}`}
                                onClick={() => setPaymentMethod("usdt")}
                                style={{
                                    background: "#ffffff",
                                    border: `1px solid ${paymentMethod === "usdt" ? theme.borderStrong : "rgba(17,20,39,0.08)"}`,
                                    borderRadius: "18px",
                                    padding: "18px",
                                    minHeight: "108px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    boxShadow: paymentMethod === "usdt"
                                        ? "0 0 0 2px rgba(233, 30, 140, 0.14), 0 12px 24px rgba(0,0,0,0.08)"
                                        : "0 10px 24px rgba(0,0,0,0.08)",
                                }}
                            >
                                <img src={process.env.PUBLIC_URL + "/assets/images/payments/usdt.png"} alt="USDT" />
                            </div>

                            <div
                                className={`payment-card ${paymentMethod === "bank" ? "active" : ""}`}
                                onClick={() => setPaymentMethod("bank")}
                                style={{
                                    background: "#ffffff",
                                    border: `1px solid ${paymentMethod === "bank" ? theme.borderStrong : "rgba(17,20,39,0.08)"}`,
                                    borderRadius: "18px",
                                    padding: "18px",
                                    minHeight: "108px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    boxShadow: paymentMethod === "bank"
                                        ? "0 0 0 2px rgba(233, 30, 140, 0.14), 0 12px 24px rgba(0,0,0,0.08)"
                                        : "0 10px 24px rgba(0,0,0,0.08)",
                                }}
                            >
                                <img src={process.env.PUBLIC_URL + "/assets/images/payments/bank-deposit.png"} alt="Bank Deposit" />
                            </div>
                        </div>

                        {/* ================= NOW PAYMENTS ================= */}
                        {paymentMethod === "now" && (
                            <div className="payment-box mb-3" style={{ background: theme.panelBg, border: `1px solid ${theme.border}`, borderRadius: "20px", padding: "20px" }}>
                                <h6 style={{ color: theme.text }}>NowPayments</h6>
                                <p style={{ color: theme.muted }}>Amount: ${total.toFixed(2)}</p>
                                <button className="btn bg-pink text-white">Pay Now</button>
                            </div>
                        )}

                        {/* ================= STRIPE ================= */}
                        {paymentMethod === "stripe" && (
                            <div className="payment-box mb-3" style={{ background: theme.panelBg, border: `1px solid ${theme.border}`, borderRadius: "20px", padding: "20px" }}>
                                <h6 style={{ color: theme.text }}>Stripe</h6>
                                <p style={{ color: theme.muted }}>Amount: ${total.toFixed(2)}</p>
                                <button className="btn bg-blue text-white">Pay Now</button>
                            </div>
                        )}

                        {/* ================= UPI ================= */}
                        {paymentMethod === "upi" && (
                            <div className="payment-box mb-3 d-flex flex-column gap-2" style={{ background: theme.panelBg, border: `1px solid ${theme.border}`, borderRadius: "20px", padding: "20px", color: theme.text }}>
                                <h6 style={{ color: theme.text }}>UPI Payment</h6>

                                <div className="row">
                                    <div className="col-lg-6 col-12 d-flex gap-2 flex-column justify-content-center align-items-center">
                                        <span>MS MS SHREE SHYAM BUILDERS</span>
                                        <img
                                            className="img-fluid"
                                            src={process.env.PUBLIC_URL + "/assets/images/payments/upi-qr.png"}
                                            alt="UPI QR"
                                        />
                                        <span className="text-center">
                                            UPI ID:<br />profxevent@indianbank
                                        </span>
                                    </div>

                                    <div className="col-lg-6 col-12 d-flex flex-column gap-3 justify-content-center align-items-start">
                                        <h6 style={{ color: theme.text }}>Upload Payment Proof for Verification</h6>
                                        <label style={{ color: theme.muted }}>Amount to be Paid</label>
                                        <input type="text" value={`$${total}`} disabled style={{ ...fieldStyle, opacity: 0.8 }} />
                                        <input type="file" onChange={(e) => setPaymentImage(e.target.files[0])} style={fieldStyle} />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* ================= USDT ================= */}
                        {paymentMethod === "usdt" && (
                            <div className="payment-box mb-3 d-flex flex-column gap-3" style={{ background: theme.panelBg, border: `1px solid ${theme.border}`, borderRadius: "20px", padding: "20px", color: theme.text }}>
                                <h6 style={{ color: theme.text }}>USDT Payment</h6>

                                <div className="row">
                                    <div className="col-lg-6 col-12 d-flex flex-column flex-lg-row gap-4 justify-content-center">
                                        <div className="d-flex flex-column align-items-center">
                                            <span>Network</span>
                                            <h6>TRON (TRC20)</h6>
                                            <img
                                                className="img-fluid mb-2"
                                                src={process.env.PUBLIC_URL + "/assets/images/payments/usdt-qr-1.png"}
                                                alt="TRON QR"
                                            />
                                            <span className="text-break text-center">
                                                TGGbNk9YvAEdguozs3EdapnEAuc1RYghio
                                            </span>
                                        </div>

                                        <div className="d-flex flex-column align-items-center">
                                            <span>Network</span>
                                            <h6>ETHEREUM (ERC20)</h6>
                                            <img
                                                className="img-fluid mb-2"
                                                src={process.env.PUBLIC_URL + "/assets/images/payments/usdt-qr-2.png"}
                                                alt="ETH QR"
                                            />
                                            <span className="text-break text-center">
                                                0x07b0a37f957b82633ce1aadb8e6752c88f115094
                                            </span>
                                        </div>
                                    </div>

                                    <div className="col-lg-6 col-12 d-flex flex-column gap-3 justify-content-center">
                                        <h6 style={{ color: theme.text }}>Upload Payment Proof for Verification</h6>
                                        <select style={fieldStyle}>
                                            <option>Select Network</option>
                                            <option>TRON (TRC20)</option>
                                            <option>ETHEREUM (ERC20)</option>
                                        </select>

                                        <label style={{ color: theme.muted }}>Amount to be Paid</label>
                                        <input type="text" value={`$${total}`} disabled style={{ ...fieldStyle, opacity: 0.8 }} />
                                        <input type="file" onChange={(e) => setPaymentImage(e.target.files[0])} style={fieldStyle} />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* ================= BANK DEPOSIT ================= */}
                        {paymentMethod === "bank" && (
                            <div className="payment-box mb-3 bank-deposit-box p-4 rounded" style={{ background: theme.panelBg, border: `1px solid ${theme.border}`, color: theme.text }}>
                                <h6 className="mb-4" style={{ color: theme.text }}>Bank Deposit</h6>

                                <div className="bank-grid gap-4">
                                    <div className="bank-details p-4 rounded border border-dashed border-pink" style={{ background: theme.panelSoft, color: theme.text }}>
                                        <p><strong>Account Holder:</strong> PROFINSUMMIT</p>
                                        <p><strong>Account Number:</strong> 8123734309</p>
                                        <p><strong>IFS Code:</strong> IDIB000R088</p>
                                        <p><strong>CBS Code:</strong> 02342</p>
                                        <p>
                                            <strong>Bank Address:</strong><br />
                                            RAJNI KHAND F3, WARD NUMBER 1,<br />
                                            RAJNI KHAND, SHARDA NAGAR,<br />
                                            RAEBARELI ROAD, LUCKNOW – 226002
                                        </p>
                                    </div>

                                    <div className="bank-upload d-flex flex-column gap-3">
                                        <h6 style={{ color: theme.text }}>Upload Payment Proof for Verification</h6>
                                        <label style={{ color: theme.muted }}>Amount to be Paid</label>
                                        <input type="text" value={`$${total}`} disabled style={{ ...fieldStyle, opacity: 0.8 }} />
                                        <input type="file" onChange={(e) => setPaymentImage(e.target.files[0])} style={fieldStyle} />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* FOOTER */}
                        <div className="modal-footer d-flex justify-content-between mt-4">
                            <button className="btn border-pink" onClick={() => setStep(1)}>
                                Back
                            </button>
                            <button className="btn bg-pink text-white" onClick={handleSubmit}>
                                Submit Deposit
                            </button>
                        </div>
                    </div>
                )}

            </div>
        </div>,
        document.getElementById("modal-root")
    );
};





export default BoothModal;
